import axios from "axios";
import {
  appendTimestamp,
  convertYearToAgeByClient,
  getSelectedClientAppliesToType,
} from "../Utilities/shared";
import {
  CUSTODIAN_TDAMERITRADE,
  MANAGED_MANAGED,
  MONTH_NUM_MAP,
} from "../Utilities/constants";
import {
  getSelectedClient,
  getClientDisplayNameById,
} from "../Utilities/shared";

const mapAccountTypeToTaxCategory = (accountType) => {
  return accountType.category;
};

export const mapFEAssetToBEAsset = (clientInformation) => (asset) => {
  return {
    id: asset.id,
    name: asset.name,
    scopeType: asset.scopeType,
    taxType: mapAccountTypeToTaxCategory(asset.accountType),
    accountType: asset.accountType.key,
    appliesTo: {
      appliesToId: asset.owner.id,
      appliesToType: getSelectedClientAppliesToType(
        asset.owner,
        clientInformation
      ),
    },
    isReinvest: asset.isReinvest ? asset.isReinvest : false,
    currentValue: asset.currentValue,
    costBasis: asset.currentValue,
    valueAsOf: new Date(asset.valueAsOfDate),
    accountCustodian:
      asset.custodian === "" ? CUSTODIAN_TDAMERITRADE : asset.custodian.key,
    accountCustodianOtherValue: asset.custodianName,
    custodianAccountNum: asset.accountNumber,
    custodianURL: asset.webAccess,
    managedStatus: asset.managed === "" ? MANAGED_MANAGED : asset.managed.key,
    managedBy: asset.managedBy,
    managedUserName: asset.clientUserName,
    managedPassword: asset.managedPassword,
    allocationModelView: asset.allocation,
    preAllocation: asset.preRetirementAllocation * 100.0,
    postAllocation: asset.postRetirementAllocation * 100.0,
    sortOrder: asset.sortOrder,
    isEdited: asset.isEdited,
    isCopied: asset.isCopy,
    accountAllocation: true,
    notes: asset.notes,
    contributionList: asset.contributions.map((element) => {
      return {
        id: element.id,
        description: element.name,
        isEdited: element.isEdited,
        amount: {
          frequency: element.frequencyType.key,
          amount: element.amount,
          inflationRate: element.inflation * 100,
          futureValue: element.futureValue,
        },
        date: {
          startDateType: "REGULAR_DATE",
          endDateType: "REGULAR_DATE",
          startYear: element.startYear,
          relativeStartDateNum: 0,
          endYear: element.endYear,
          relativeEndDateNum: 0,
          startMonth: MONTH_NUM_MAP["Jan"],
          endMonth: MONTH_NUM_MAP["Dec"],
        },
        employerMatch: element.employerMatch,
        matchAmount:
          element.matchType.key === "AMOUNT"
            ? element.matchAmount
            : element.matchAmount * 100,
        matchCap: element.matchCap,
        matchType: element.matchType.key,
      };
    }),
    withdrawalList: [],
    transferViewList: [],
  };
};

export const mapBEAssetToFEAsset = (clientInformation) => (asset, index) => {
  return {
    id: asset.id,
    name: asset.name,
    scopeType: asset.scopeType,
    taxCategory: { key: asset.taxType, name: "Taxable" },
    accountType: { key: asset.accountType, name: "401K" },
    isReinvest: asset.isReinvest,
    owner: {
      id: asset.appliesTo.appliesToId ? asset.appliesTo.appliesToId : 0,
      name: getClientDisplayNameById(
        asset.appliesTo.appliesToId,
        clientInformation
      ),
    },
    currentValue: asset.currentValue,
    costBasis: asset.costBasis,
    valueAsOfDate: asset.valueAsOf,
    custodian: { key: asset.accountCustodian, name: "401K" },
    custodianName: asset.accountCustodianOtherValue,
    accountNumber: asset.custodianAccountNum,
    webAccess: asset.custodianURL,
    managed: { key: asset.managedStatus, name: "MANAGED" },
    managedBy: asset.managedBy,
    clientUserName: asset.managedUserName,
    clientPassword: asset.managedPassword,
    usedInPlans: [],
    preRetirementAllocation: asset.preAllocation / 100,
    postRetirementAllocation: asset.postAllocation / 100,
    isEdited: false,
    inEdit: true,
    editId: index,
    isCopy: false,
    sortOrder: asset.sortOrder,
    isExpanded: false,
    allocation: asset.allocationModelView,
    accountAllocation: true,
    notes: asset.notes,
    contributions: asset.contributionList.map((element, contributionIndex) => {
      return {
        id: element.id,
        editId: contributionIndex,
        frequencyType: { key: element.amount.frequency, name: "Yearly" },
        amount: element.amount.amount,
        employerMatch: element.employerMatch,
        matchType: { key: element.matchType, name: "Amount" },
        matchAmount:
          element.matchType === "AMOUNT"
            ? element.matchAmount
            : element.matchAmount / 100,
        inflation: element.amount.inflationRate,
        futureValue: element.amount.futureValue,
        matchCap: element.matchCap,
        description: "description",
        startYear: element.date.startYear,
        startAge: convertYearToAgeByClient(
          element.date.startYear,
          getSelectedClient(
            { id: asset.appliesTo.appliesToId },
            clientInformation
          ),
          "Jan"
        ),
        endYear: element.date.endYear,
        endAge: convertYearToAgeByClient(
          element.date.endYear,
          getSelectedClient(
            { id: asset.appliesTo.appliesToId },
            clientInformation
          ),
          "Dec"
        ),
        interval: asset.interval,
      };
    }),
    withdrawals: [],
    transfers: [],
  };
};

export default (baseUrl, clientInformation) => {
  return {
    getAssets: (advisorId, clientId) => {
      console.log("Assets - getAssets");

      return new Promise((resolve, reject) => {
        const targetUrl = appendTimestamp(
          `${baseUrl}/advisor/${advisorId}/client/${clientId}/accounts`
        );

        axios.get(targetUrl).then((response) => {
          resolve(
            response.data.payload.map(mapBEAssetToFEAsset(clientInformation))
          );
        });
      });
    },
    deleteAssets: (advisorId, clientId, accountIdList) => {
      console.log("Assets - deleteAssets");

      return new Promise((resolve, reject) => {
        const targetUrl = `${baseUrl}/advisor/${advisorId}/client/${clientId}/account/batch-delete`;

        axios.post(targetUrl, accountIdList).then(() => resolve({}));
      });
    },
    saveAssets: (advisorId, clientId, accountList) => {
      console.log("Assets - saveAssets");

      return new Promise((resolve, reject) => {
        const targetUrl = `${baseUrl}/advisor/${advisorId}/client/${clientId}/accounts`;
        const payload = accountList.map(mapFEAssetToBEAsset(clientInformation));

        axios.post(targetUrl, payload).then((response) => {
          const accountList = response.data.payload.map(
            mapBEAssetToFEAsset(clientInformation)
          );
          resolve(accountList);
        });
      });
    },
  };
};